/**
 * `useCreateApprovalChain` mutation hook.
 *
 * Wraps the raw `createApprovalChain` service fn (per AD-23). Invalidates every
 * approval chain query under the project (`['project', projectId, 'approval-chains']`) so
 * the list refreshes with the new definition.
 *
 * When `autoApplyToExistingFolders` is set, the BE also assigns the approval chain to
 * every unassigned asset in the project, so the folder + asset-approval-chain caches
 * are busted too — see `invalidate-project-chain-assignments.ts`.
 *
 * Side effects: NONE. Toast + navigation UX belong to the caller (the editor).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  APPROVAL_CHAIN_QUERY_KEYS,
  type CreateApprovalChainInput,
} from '@/entities/approval-chain';

import { invalidateProjectChainAssignments } from './invalidate-project-chain-assignments';
import { createApprovalChain } from './approval-chains.api';

export { createApprovalChain };

interface UseCreateApprovalChainOptions {
  projectId: string;
}

export function useCreateApprovalChain(options: UseCreateApprovalChainOptions) {
  const queryClient = useQueryClient();
  const { projectId } = options;

  return useMutation({
    mutationFn: (body: CreateApprovalChainInput) => createApprovalChain(projectId, body),
    onSuccess: (_data, body) => {
      void queryClient.invalidateQueries({
        queryKey: APPROVAL_CHAIN_QUERY_KEYS.all(projectId),
      });

      if (body.autoApplyToExistingFolders) {
        invalidateProjectChainAssignments(queryClient, projectId);
      }
    },
  });
}
